import { writable } from 'svelte/store';
import type { TimeSeriesMeta, TimeSeriesData, ForecastData, AIAnalysisData } from '../lib/types';
import { timeSeriesApi, forecastApi, aiApi } from '../lib/api';

// Store for holding all time series
export const allTimeSeries = writable<TimeSeriesMeta[]>([]);

// Store for the currently selected time series
export const currentTimeSeries = writable<TimeSeriesData | null>(null);

// Store for forecasts of the current time series
export const forecasts = writable<ForecastData[]>([]);

// Store for AI analyses of the current time series
export const analyses = writable<AIAnalysisData[]>([]);

// Store for AI insights of the current time series
export const aiAnalyses = writable<AIAnalysisData[]>([]);

// Loading and error states
export const isLoading = writable<boolean>(false);
export const error = writable<Error | null>(null);

/**
 * Helper function to get a fixed user ID for testing
 * In a real application, this would come from the authentication system
 */
export function generateDummyUserId(): number {
    // Fixed ID for our test user
    return 1;
}

/**
 * Fetch a time series by ID
 */
export async function fetchTimeSeries(id: string | number, includeData: boolean = false): Promise<void> {
    isLoading.set(true);
    error.set(null);
    
    try {
        const data = await timeSeriesApi.getById(id, includeData);
        currentTimeSeries.set(data);
    } catch (err) {
        // Handle the error properly to prevent [object Object] display
        if (err instanceof Error) {
            // Use the error as is, with all its properties
            error.set(err);
        } else if (typeof err === 'object' && err !== null) {
            // Convert object errors to proper Error objects
            try {
                // Try to create a detailed error message
                const errorMessage = JSON.stringify(err);
                const formattedError = new Error(errorMessage);
                // Store the original error object for debugging
                (formattedError as any).responseData = err;
                error.set(formattedError);
            } catch {
                // If JSON stringify fails, use a generic error
                const genericError = new Error('Error fetching time series data');
                (genericError as any).responseData = { errorType: typeof err };
                error.set(genericError);
            }
        } else if (err) {
            // Handle primitive error types
            const simpleError = new Error(String(err));
            error.set(simpleError);
        } else {
            // Fallback for undefined/null errors
            error.set(new Error('Failed to load time series: Unknown error'));
        }
        
        console.error('Error fetching time series:', err);
    } finally {
        isLoading.set(false);
    }
}

/**
 * Fetch forecasts for a time series
 */
export async function fetchForecasts(timeSeriesId: string | number): Promise<void> {
    try {
        const response = await forecastApi.getAll(timeSeriesId);
        forecasts.set(response.forecasts);
    } catch (err) {
        // Log errors but don't update the global error state
        console.error('Error fetching forecasts:', err);
        // Return empty array for forecasts
        forecasts.set([]);
    }
}

/**
 * Fetch analyses for a time series
 */
export async function fetchAnalyses(timeSeriesId: string | number): Promise<void> {
    try {
        const response = await aiApi.getAll(timeSeriesId);
        analyses.set(response.analyses);
    } catch (err) {
        // Log errors but don't update the global error state
        console.error('Error fetching analyses:', err);
        // Return empty array for analyses
        analyses.set([]);
    }
}

/**
 * Fetch AI analyses for a time series
 */
export async function fetchAIAnalyses(timeSeriesId: string | number): Promise<void> {
    try {
        const response = await aiApi.getAll(timeSeriesId);
        aiAnalyses.set(response.analyses);
    } catch (err) {
        // Log errors but don't update the global error state
        console.error('Error fetching AI analyses:', err);
        // Return empty array for AI analyses
        aiAnalyses.set([]);
    }
}

/**
 * Updates a time series in the allTimeSeries store
 */
export function updateTimeSeriesInList(updatedTimeSeries: TimeSeriesMeta): void {
    allTimeSeries.update(list => {
        const index = list.findIndex(ts => ts.id === updatedTimeSeries.id);
        if (index !== -1) {
            list[index] = updatedTimeSeries;
        }
        return list;
    });
}

/**
 * Removes a time series from the allTimeSeries store
 */
export function removeTimeSeriesFromList(id: number): void {
    allTimeSeries.update(list => list.filter(ts => ts.id !== id));
}

/**
 * Adds a new forecast to the forecasts store
 */
export function addForecast(forecast: ForecastData): void {
    forecasts.update(list => [forecast, ...list]);
}

/**
 * Removes a forecast from the forecasts store
 */
export function removeForecast(id: number): void {
    forecasts.update(list => list.filter(f => f.id !== id));
}

/**
 * Adds a new analysis to the analyses store
 */
export function addAnalysis(analysis: AIAnalysisData): void {
    analyses.update(list => [analysis, ...list]);
}

/**
 * Reset all stores (useful for cleanup or logout)
 */
export function resetStores(): void {
    allTimeSeries.set([]);
    currentTimeSeries.set(null);
    forecasts.set([]);
    analyses.set([]);
    aiAnalyses.set([]);
    isLoading.set(false);
    error.set(null);
}
